import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faSpinner, faExclamationTriangle, faCheckCircle, faArrowLeft, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import axios from '../config/axiosConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { useBranding } from '../contexts/BrandingContext';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const { logo, appName, primaryColor } = useBranding();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || t('common.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            {logo ? (
              <img
                src={logo}
                alt={appName}
                className="h-20 w-auto max-w-[200px] object-contain"
              />
            ) : (
              <div className="bg-gradient-to-r from-red-600 p-4 rounded-full" style={{ backgroundImage: `linear-gradient(to right, #dc2626, ${primaryColor})` }}>
                <FontAwesomeIcon icon={faShieldAlt} className="h-12 w-12 text-white" />
              </div>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {t('auth.forgotPasswordTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            {t('auth.forgotPasswordSubtitle')}
          </p>
        </div>

        <div className="mt-8 bg-gray-800 py-8 px-6 shadow-xl rounded-lg border border-gray-700">
          {success ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-900/50 mb-4">
                <FontAwesomeIcon icon={faCheckCircle} className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">{t('auth.checkYourEmail')}</h3>
              <p className="text-gray-400 mb-6">
                {t('auth.resetEmailSent')}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t('auth.linkExpires')}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center hover:opacity-80"
                style={{ color: primaryColor }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                {t('auth.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded flex items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  {error}
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                    {t('auth.emailAddress')}
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-gray-500" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={t('auth.enterEmailAddress')}
                    />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('auth.sending')}
                      </>
                    ) : (
                      t('auth.sendResetLink')
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm hover:opacity-80"
                  style={{ color: primaryColor }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Security note */}
        <div className="text-center text-xs text-gray-500">
          <p>{t('auth.securityNote')}</p>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-4">
          <p>{appName}. Purple Team Operations by Zerberos</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
